import { decryptJson, decryptToBlob, encryptBlob, encryptJson } from '@/services/crypto/encryption';
import { bytesToBase64, base64ToBytes } from '@/services/crypto/base64';
import { cvRepository } from '@/services/db/repositories/cvRepository';
import { photoRepository } from '@/services/db/repositories/photoRepository';
import {
  deleteEncryptedCv,
  getEncryptedCv,
  listEncryptedCvs,
  setEncryptedCv,
  watchEncryptedCvs,
  type EncryptedCvDoc,
} from '@/services/firebase/firestore';
import {
  deleteRemotePhoto,
  downloadEncryptedPhoto,
  uploadEncryptedPhoto,
} from '@/services/firebase/storage';
import { useAuthStore } from '@/stores/authStore';
import type { CvData } from '@/types/cv';

const PHOTO_IV_REGEX = /^[A-Za-z0-9+/=]+$/;

function requireSession(): { uid: string; key: CryptoKey } {
  const state = useAuthStore.getState();
  if (!state.user) throw new Error('Brak zalogowanego użytkownika');
  if (!state.encryptionKey) throw new Error('Klucz szyfrujący nie jest odblokowany');
  return { uid: state.user.uid, key: state.encryptionKey };
}

interface UploadOptions {
  uploadPhoto?: boolean;
}

export async function uploadCv(cv: CvData, options: UploadOptions = {}): Promise<void> {
  const { uid, key } = requireSession();
  useAuthStore.getState().setSyncStatus('syncing');
  try {
    let photoIv: string | undefined;
    let photoMimeType: string | undefined;

    if (options.uploadPhoto !== false && cv.personalInfo.photoId) {
      const photo = await photoRepository.getById(cv.personalInfo.photoId);
      if (photo) {
        const { ciphertext, iv } = await encryptBlob(key, photo.blob);
        await uploadEncryptedPhoto(uid, cv.personalInfo.photoId, ciphertext);
        photoIv = bytesToBase64(iv);
        photoMimeType = photo.mimeType;
      }
    }

    const encrypted = await encryptJson(key, cv);
    const doc: EncryptedCvDoc = {
      encryptedData: encrypted.ciphertext,
      iv: encrypted.iv,
      updatedAt: cv.metadata.updatedAt,
      schemaVersion: cv.metadata.schemaVersion,
      ...(photoIv ? { photoIv } : {}),
      ...(photoMimeType ? { photoMimeType } : {}),
    };
    await setEncryptedCv(uid, cv.id, doc);
    useAuthStore.getState().markSynced();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    useAuthStore.getState().setSyncStatus('error', message);
    throw err;
  }
}

export async function downloadCv(cvId: string): Promise<CvData | null> {
  const { uid, key } = requireSession();
  const remoteDoc = await getEncryptedCv(uid, cvId);
  if (!remoteDoc) return null;
  const cv = await decryptJson<CvData>(key, {
    ciphertext: remoteDoc.encryptedData,
    iv: remoteDoc.iv,
  });

  if (cv.personalInfo.photoId && remoteDoc.photoIv && PHOTO_IV_REGEX.test(remoteDoc.photoIv)) {
    const ciphertext = await downloadEncryptedPhoto(uid, cv.personalInfo.photoId);
    if (ciphertext) {
      const blob = await decryptToBlob(
        key,
        ciphertext,
        base64ToBytes(remoteDoc.photoIv),
        remoteDoc.photoMimeType ?? 'image/jpeg',
      );
      const existing = await photoRepository.getById(cv.personalInfo.photoId);
      if (!existing) {
        const photo = await photoRepository.saveRaw(blob, remoteDoc.photoMimeType ?? 'image/jpeg');
        cv.personalInfo.photoId = photo.id;
      }
    }
  }
  return cv;
}

export async function pullAllFromCloud(): Promise<{ pulled: number }> {
  const { uid, key } = requireSession();
  const remote = await listEncryptedCvs(uid);
  let pulled = 0;
  for (const doc of remote) {
    try {
      const cv = await decryptJson<CvData>(key, {
        ciphertext: doc.encryptedData,
        iv: doc.iv,
      });
      const local = await cvRepository.getById(cv.id);
      const remoteUpdated = doc.updatedAt ?? cv.metadata.updatedAt;
      const localUpdated = local?.metadata.updatedAt ?? '';
      if (!local || remoteUpdated > localUpdated) {
        if (cv.personalInfo.photoId && doc.photoIv) {
          const ciphertext = await downloadEncryptedPhoto(uid, cv.personalInfo.photoId);
          if (ciphertext) {
            const blob = await decryptToBlob(
              key,
              ciphertext,
              base64ToBytes(doc.photoIv),
              doc.photoMimeType ?? 'image/jpeg',
            );
            const existing = await photoRepository.getById(cv.personalInfo.photoId);
            if (!existing) {
              await photoRepository.saveRaw(blob, doc.photoMimeType ?? 'image/jpeg');
            }
          }
        }
        await cvRepository.save(cv);
        pulled += 1;
      }
    } catch (err) {
      console.error('Nie można odszyfrować CV', doc.id, err);
    }
  }
  useAuthStore.getState().markSynced();
  return { pulled };
}

export async function pushAllToCloud(): Promise<{ pushed: number }> {
  requireSession();
  const local = await cvRepository.getAll();
  let pushed = 0;
  for (const cv of local) {
    await uploadCv(cv);
    pushed += 1;
  }
  return { pushed };
}

export interface ConflictInfo {
  cvId: string;
  localUpdatedAt: string;
  remoteUpdatedAt: string;
}

export async function detectConflict(cvId: string): Promise<ConflictInfo | null> {
  const { uid } = requireSession();
  const [local, remote] = await Promise.all([
    cvRepository.getById(cvId),
    getEncryptedCv(uid, cvId),
  ]);
  if (!local || !remote) return null;
  if (remote.updatedAt > local.metadata.updatedAt) {
    return {
      cvId,
      localUpdatedAt: local.metadata.updatedAt,
      remoteUpdatedAt: remote.updatedAt,
    };
  }
  return null;
}

export async function deleteCvRemote(cvId: string, photoId?: string): Promise<void> {
  const { uid } = requireSession();
  await deleteEncryptedCv(uid, cvId);
  if (photoId) {
    await deleteRemotePhoto(uid, photoId);
  }
}

export async function watchRemoteCvs(
  onIncoming: (cv: CvData) => void | Promise<void>,
): Promise<() => void> {
  const { uid, key } = requireSession();
  return watchEncryptedCvs(uid, (docs) => {
    void (async () => {
      for (const doc of docs) {
        try {
          const cv = await decryptJson<CvData>(key, {
            ciphertext: doc.encryptedData,
            iv: doc.iv,
          });
          await onIncoming(cv);
        } catch (err) {
          console.error('Nie można odszyfrować CV z listenera', doc.id, err);
        }
      }
    })();
  });
}
